import angular from "angular";
import addChangeFundraiserItemDialogTemplate from "./AddChangeFundraiserItemDialog.html";

/*****************************************************************************
 * Service: AddChangeFundraiserItemDialog */
/**
 * @ngdoc     service
 * @module    csm.books.fundraisers
 * @name      csm.books.fundraisers.service:AddChangeFundraiserItemDialog
 * @description
 * This service will display a dialog to manage a FundraiserItem. When adding a
 * new item sale, if there are unsold items, the `ItemsNotSoldDialog` is displayed
 * with the list of unsold items prompting to user to pick one.
 *****************************************************************************/
/* @ngInject */ export function AddChangeFundraiserItemDialogService($q, $uibModal, FundraiserItemTypes, ItemsNotSoldDialog) {
  /*****************************************************************************
   * show */
  /**
   * @ngdoc    function
   * @name     show
   * @methodOf csm.books.fundraisers.service:AddChangeFundraiserItemDialog
   * @description
   * @param    {Number} addMode         True=Add new item
   * @param    {Array}  item            Item to manipulate. If adding a new item,
   *                                    a empty FundraiserItem with the fundraiserID
   *                                    and type properties already set is expected.
   * @param    {Array}  itemsNotSold    Items Not Sold List. Shown only on the
   *                                    add of a sales item.
   * @return   {Promise}                Resolves upon successful add/update
   *                                    of fundraiser item. Item object is passed
   *                                    as resolve data.
   *****************************************************************************/
  this.show = function (addMode, item, itemsNotSold) {
    /*-------------------------------------------------------*/
    /* Function to display the modal dialog and return the   */
    /* promise resolved when the dialog is closed/dismissed. */
    /*-------------------------------------------------------*/
    function openModal(itemName) {
      return $uibModal.open({
        animation: false,
        backdrop: 'static',
        component: 'addChangeFundraiserItem',
        resolve: {
          addMode: function () {
            return addMode;
          },
          item: function () {
            return item;
          },
          itemName: function () {
            return itemName;
          },
          itemsNotSold: function () {
            return itemsNotSold;
          }
        }
      }).result;
    }

    /*------------------------------------------------------------------*/
    /* Display the list of items not sold if this an add of an item     */
    /* sale and there are item costs that haven't been set up as sales. */
    /*------------------------------------------------------------------*/
    if (
      addMode &&
      item.type == FundraiserItemTypes.ITEM_SALE &&
      itemsNotSold.length > 0
    ) {
      return ItemsNotSoldDialog.show(itemsNotSold).then(function (itemName) {
        /*----------------------------------------*/
        /* Continue with this modal, prepopulated */
        /* with the user's item selection.        */
        /*----------------------------------------*/
        return openModal(itemName);
      });
    } else return openModal();
  };
}

/*****************************************************************************
 * Component: addChangeFundraiserItem */
/**
 * @ngdoc     component
 * @module    csm.books.fundraisers
 * @name      csm.books.fundraisers.component:addChangeFundraiserItem
 * @param  {Object} modalInstance $uibModal instance from $uibModal.open method
 * @param  {Object} resolve       Resolve object passed from $uibModal.open method
 * @description
 * This component will handle the management of a FundraiserItem data. This component
 * is expected to be used with $uibModal.
 *****************************************************************************/
export const addChangeFundraiserItemDialogComponent = {
  template: addChangeFundraiserItemDialogTemplate,
  bindings: { modalInstance: '<', resolve: '<' },
  controller: AddChangeFundraiserItemController
}

/*****************************************************************************
 * Controller: AddChangeFundraiserItemController */
/**
 * @ngdoc     controller
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:AddChangeFundraiserItemController
 * @description
 * Controller for the AddChangeFundraiserItem component
 *****************************************************************************/
/* @ngInject */ function AddChangeFundraiserItemController($injector) {
  var $ctrl = this;
  $ctrl.validators = {};

  $ctrl.$onInit = $injector.invoke($onInit, null, { $ctrl: $ctrl });
  $ctrl.save = $injector.invoke(save, null, { $ctrl: $ctrl });
}

/*****************************************************************************
 * $onInit */
/**
 * @ngdoc     method
 * @name      $onInit
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:AddChangeFundraiserItemController
 * @description
 * Called by angular when the component is ready. This will set up some
 * properties from the bound values.
 *****************************************************************************/
/* @ngInject */ function $onInit($ctrl, Alert, FundraiserItemTypes) {
  return function () {
    /*----------------------------------------------------*/
    /* Data passed to the dialog from the resolve object. */
    /*----------------------------------------------------*/
    $ctrl.addMode = $ctrl.resolve.addMode;
    $ctrl.item = $ctrl.resolve.item;
    $ctrl.itemsNotSold = $ctrl.resolve.itemsNotSold;

    /*-----------------------------------*/
    /* Item name is provided when adding */
    /* a new item, set it on the item.   */
    /*-----------------------------------*/
    if ($ctrl.addMode && $ctrl.resolve.itemName)
      angular.extend($ctrl.item, { itemName: $ctrl.resolve.itemName });

    /*---------------------*/
    /* Modal dialog title. */
    /*---------------------*/
    $ctrl.title =
      ($ctrl.addMode ? 'Add ' : 'Change ') +
      ($ctrl.item.type == FundraiserItemTypes.ITEM_COST
        ? 'Item Cost'
        : $ctrl.item.type == FundraiserItemTypes.FIXED_ITEM
          ? 'Fixed Item'
          : 'Item Sale');

    /*-----------------------------------*/
    /* Add passthrough to dismiss method */
    /*-----------------------------------*/
    $ctrl.dismiss = $ctrl.modalInstance.dismiss;

    /*--------------------------------------*/
    /* Flag to place focus on the item name */
    /* field after initilization is ready.  */
    /*--------------------------------------*/
    $ctrl.nameHasFocus = true;
  };
}

/*****************************************************************************
 * save */
/**
 * @ngdoc     method
 * @name      save
 * @module    csm.books.fundraisers
 * @methodof  csm.books.fundraisers.controller:AddChangeFundraiserItemController
 * @description
 * Called when the Add/Change button is clicked. This will validate the UI before
 * calling the save service. When everything is updated, the component will call
 * the dialog close passing in the fundraiser item model.
 *****************************************************************************/
/* @ngInject */ function save(Alert, BusyScreen, FundRaiser, Tools, $ctrl) {
  return function () {
    /*------------------*/
    /* Validate inputs. */
    /*------------------*/
    if (!$ctrl.validators.validate()) return;

    /*-----------------------------*/
    /* Save item and close dialog. */
    /*-----------------------------*/
    BusyScreen.setBusy(true);
    FundRaiser.saveFundraiserItem($ctrl.item)
      .then(
        function (id) {
          /*-------------------------------------------*/
          /* Update the item with a subtotal property. */
          /*-------------------------------------------*/
          $ctrl.item.subtotal = Tools.roundMoney(
            $ctrl.item.qty * $ctrl.item.amount
          );
          $ctrl.item.id = id;
          $ctrl.modalInstance.close($ctrl.item);
        },
        function (resp) {
          Alert.error(resp.parsedResponse.message(), 'Add item failed');
        }
      )
      .finally(function () {
        BusyScreen.setBusy(false);
      });
  };
}
